import type { UnitSystem } from '@pn/core/domain/types';
import type { WorkspaceItem } from '@pn/core/domain/workspace';
import { loadItem } from '@pn/core/loaders/workspace/loadWorkspaceItem';
import {
  type Output,
  visualizeLoadedWorkspaceItem,
} from '@pn/core/operations/workspace/visualizeLoadedWorkspaceItem';
import {
  useAppStorage,
  useCurrentUserStorage,
  useTableStorage,
  workspaceActions,
} from '@pn/core/storage';
import React from 'react';

type Params = {
  item: WorkspaceItem;
  autoTrigger?: boolean;
  silent?: boolean;
  rethrow?: boolean;
};

export type VisualizeWorkspaceItem = (params: Params) => Promise<Output>;

type Props = {
  tableFields?: Record<string, string[] | undefined>;
  tableOnly?: boolean;
};

export function useVisualizeWorkspaceItem({
  tableFields: tableFieldsParam,
  tableOnly = false,
}: Props): VisualizeWorkspaceItem {
  const { unitSystem, pageSize } = useAppStorage();
  const { stackCompanyId } = useCurrentUserStorage();
  const { tableFields: storageTableFields } = useTableStorage();

  const tableFields = tableFieldsParam ?? storageTableFields;

  return React.useCallback(
    async ({
      item,
      autoTrigger = false,
      silent = false,
      rethrow = false,
    }: Params) => {
      workspaceActions().markAsProcessed(item.id);
      workspaceActions().markAsUnfinalized(item.id);

      const fields = tableFields[item.dataType] ?? [];

      workspaceActions().updateQueryProperty(item.id, { unitSystem, fields });
      const updatedItem = setUpdatedProperties(item, unitSystem, fields);

      const loadedItem = await loadItem(updatedItem, stackCompanyId);

      return visualizeLoadedWorkspaceItem({
        item: loadedItem,
        pageSize,
        extraOptions: { autoTrigger, tableOnly, silent, rethrow },
      });
    },
    [tableOnly, unitSystem, pageSize, stackCompanyId, tableFields]
  );
}

function setUpdatedProperties(
  item: WorkspaceItem,
  unitSystem: UnitSystem,
  fields: string[]
): WorkspaceItem {
  return {
    ...item,
    query: {
      ...item.query,
      unitSystem,
      fields,
    },
  };
}
